import React, { useMemo, useState } from 'react';
import { ThresholdBar, PhaseType } from './ThresholdBar';
import { HistoryLineChart } from './HistoryLineChart';
import type { LinkedAmqpOutlet, OutletHistoryResponse } from '../../../../../services/transformerAmqpService';
import { VOLTAGE_OPERATIONAL_MAX_V, VOLTAGE_WARNING_HIGH_V } from '../../../../../utils/voltageConstants';
import { parseUtcTimestampMs } from '../../../../../utils/timeUtils';

interface PhaseBarChartProps {
  outlet: LinkedAmqpOutlet;
  historyData: OutletHistoryResponse | null;
  startDate: Date;
  endDate: Date;
  isLive: boolean;
}

interface PhaseData {
  phase: PhaseType;
  currentValue: number;
  minValue?: number;
  maxValue?: number;
  unit: string;
  label?: string;
  /** Per-phase threshold overrides (calculated later) */
  maxThreshold?: number;
  warningThreshold?: number;
}

interface MeasurementGroup {
  category: string;
  label: string;
  unit: string;
  phases: PhaseData[];
  maxThreshold: number;
  warningThreshold?: number;
}

/**
 * Extract phase from measurementType or iecName
 * Handles patterns like:
 * - measurementType: current_phase_a, voltage_phase_b, power_phase_c, current_neutral
 * - iecName: AVMMXU1.A.PhsA.cVal.mag, AVMMXU1.VA.PhsB.cVal.mag
 */
const getMeasurementPhase = (measurementType: string | null, iecName: string | null): PhaseType | null => {
  const type = (measurementType ?? '').toLowerCase();
  const iec = (iecName ?? '').toLowerCase();

  // Check measurementType patterns
  if (type.includes('phase_a') || type.includes('_a_') || type.endsWith('_a')) return 'L1';
  if (type.includes('phase_b') || type.includes('_b_') || type.endsWith('_b')) return 'L2';
  if (type.includes('phase_c') || type.includes('_c_') || type.endsWith('_c')) return 'L3';
  if (type.includes('neutral') || type.includes('_n_') || type.endsWith('_n')) return 'N';

  // Check IEC name patterns (PhsA, PhsB, PhsC, neut)
  if (iec.includes('.phsa.') || iec.includes('.phsa')) return 'L1';
  if (iec.includes('.phsb.') || iec.includes('.phsb')) return 'L2';
  if (iec.includes('.phsc.') || iec.includes('.phsc')) return 'L3';
  if (iec.includes('.neut.') || iec.includes('.neut')) return 'N';

  return null;
};

/**
 * Get measurement category from measurementType, iecName or bedeutung
 * Categories:
 * - A = current (Strom)
 * - W = active power (Wirkleistung)
 * - VA = apparent power (Scheinleistung)
 * - Var = reactive power (Blindleistung)
 * - PhV = voltage (Spannung)
 */
const getMeasurementCategory = (measurementType: string | null, iecName: string | null, bedeutung: string | null): string => {
  const type = (measurementType ?? '').toLowerCase();
  const iec = (iecName ?? '').toLowerCase();
  const bed = (bedeutung ?? '').toLowerCase();

  // Check IEC name category (format: TYPE.CATEGORY.PHASE.VALUE)
  // e.g., AVMMXU1.VA.PhsA.cVal.mag -> category is VA
  const iecParts = iec.split('.');
  if (iecParts.length >= 2) {
    const iecCat = iecParts[1].toLowerCase();
    if (iecCat === 'va' || iecCat === 'totva') return 'apparent_power';
    if (iecCat === 'var' || iecCat === 'totvar') return 'reactive_power';
    if (iecCat === 'w' || iecCat === 'totw') return 'active_power';
    if (iecCat === 'a') return 'current';
    if (iecCat === 'phv') return 'voltage';
  }

  // Check measurementType
  if (type.includes('apparent') || bed.includes('scheinleistung')) return 'apparent_power';
  if (type.includes('reactive') || bed.includes('blindleistung')) return 'reactive_power';
  if (type.includes('power') || bed.includes('wirkleistung')) return 'active_power';
  if (type.includes('current') || bed.includes('strom')) return 'current';
  if (type.includes('voltage') || bed.includes('spannung')) return 'voltage';

  return 'other';
};

/**
 * Check if measurement is a total (not per-phase)
 */
const isTotalMeasurement = (measurementType: string | null, iecName: string | null): boolean => {
  const type = (measurementType ?? '').toLowerCase();
  const iec = (iecName ?? '').toLowerCase();

  // Check measurementType
  if (type.includes('total') || type.includes('_sum') || type.endsWith('_t')) return true;

  // Check IEC name for Tot prefix (TotW, TotVA, TotVar)
  const iecParts = iec.split('.');
  if (iecParts.length >= 2) {
    const iecCat = iecParts[1].toLowerCase();
    if (iecCat.startsWith('tot')) return true;
  }

  return false;
};

// Category labels in German
const categoryLabels: Record<string, string> = {
  apparent_power: 'Scheinleistung',
  reactive_power: 'Blindleistung',
  active_power: 'Wirkleistung',
  current: 'Strom',
  voltage: 'Spannung',
  other: 'Sonstige'
};

// Default units per category
const categoryUnits: Record<string, string> = {
  apparent_power: 'kVA',
  reactive_power: 'kvar',
  active_power: 'kW',
  current: 'A',
  voltage: 'V',
  other: ''
};

// Phase order for consistent display
const phaseOrder: PhaseType[] = ['L1', 'L2', 'L3', 'N'];

/**
 * Lemonbeat-style phase bar chart component.
 * Groups measurements by category (Strom, Leistung, etc.) and displays
 * horizontal threshold bars for each phase (L1, L2, L3, N).
 */
// Filter options for measurement categories
type CategoryFilter = 'all' | 'apparent_power' | 'active_power' | 'reactive_power' | 'current' | 'voltage';

const filterLabels: Record<CategoryFilter, string> = {
  all: 'Alle',
  apparent_power: 'Scheinleistung',
  active_power: 'Wirkleistung',
  reactive_power: 'Blindleistung',
  current: 'Strom',
  voltage: 'Spannung'
};

export const PhaseBarChart: React.FC<PhaseBarChartProps> = ({
  outlet,
  historyData,
  startDate,
  endDate,
  isLive
}) => {
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

  const measurementGroups = useMemo<MeasurementGroup[]>(() => {
    const measurements = outlet.measurements ?? [];
    const rangeStart = startDate.getTime();
    const rangeEnd = endDate.getTime();

    // Helper to get aggregated values from history within [startDate, endDate]
    const getHistoryValue = (valueId: number): { current: number; min: number; max: number } | null => {
      if (!historyData) return null;
      const series = historyData.series.find(s => s.valueId === valueId);
      if (!series) return null;

      // Filter data points within the date range
      const pointsInRange = series.dataPoints.filter(dp => {
        const t = parseUtcTimestampMs(dp.hourTimestamp);
        return t >= rangeStart && t <= rangeEnd;
      });

      if (pointsInRange.length === 0) return null;

      const allMin = Math.min(...pointsInRange.map(dp => dp.minValue));
      const allMax = Math.max(...pointsInRange.map(dp => dp.maxValue));
      // Average of all hourly averages in the range
      const avgValue = pointsInRange.reduce((sum, dp) => sum + (dp.minValue + dp.maxValue) / 2, 0) / pointsInRange.length;

      return { current: avgValue, min: allMin, max: allMax };
    };

    // Group measurements by category and phase/total
    // Key format: "category:phase" or "category:total"
    const measurementMap = new Map<string, PhaseData>();

    // Process measurements
    for (const m of measurements) {
      const category = getMeasurementCategory(m.measurementType, m.iecName, m.bedeutung);
      const isTotal = isTotalMeasurement(m.measurementType, m.iecName);
      const phase = isTotal ? null : getMeasurementPhase(m.measurementType, m.iecName);
      const unit = m.einheit || m.unit || categoryUnits[category] || '';

      // Create key for this measurement
      const key = isTotal ? `${category}:total` : phase ? `${category}:${phase}` : null;
      if (!key) continue;

      // Get current value
      let currentValue: number;
      let minValue: number | undefined;
      let maxValue: number | undefined;

      if (isLive) {
        // Live: use current measurement value
        currentValue = m.value;
        // Add historical min/max from the selected range
        const histData = getHistoryValue(m.valueId);
        if (histData) {
          minValue = histData.min;
          maxValue = histData.max;
        }
      } else {
        // Historical: use aggregated data from the range
        const histData = getHistoryValue(m.valueId);
        if (histData) {
          currentValue = histData.current;
          minValue = histData.min;
          maxValue = histData.max;
        } else {
          // Fallback to current measurement if no history in range
          currentValue = m.value;
        }
      }

      measurementMap.set(key, {
        phase: isTotal ? 'Ges' : phase!,
        currentValue,
        minValue,
        maxValue,
        unit,
        label: m.bedeutung || undefined
      });
    }

    // Group by category
    const categoryMap = new Map<string, PhaseData[]>();
    for (const [key, data] of measurementMap) {
      const category = key.split(':')[0];
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(data);
    }

    // Convert to array and calculate thresholds
    const groups: MeasurementGroup[] = [];
    const ratingPowerKva = outlet.ratingPowerKva || null; // 0 or null = not provided
    const fuseSizeA = outlet.fuseSizeA || null; // 0 or null = not provided
    const hasDeviceThresholds = outlet.thresholdWarningPercent != null && outlet.thresholdCriticalPercent != null;
    const warningPercent = hasDeviceThresholds ? outlet.thresholdWarningPercent! / 100 : undefined;
    const criticalPercent = hasDeviceThresholds ? outlet.thresholdCriticalPercent! / 100 : undefined;

    // Helper: data-driven scale max when no device metadata is available
    const dataMax = (phs: PhaseData[]) => {
      const maxVal = Math.max(...phs.map(p => Math.abs(p.maxValue ?? p.currentValue)));
      return maxVal > 0 ? maxVal * 1.2 : 100;
    };

    for (const [category, phases] of categoryMap) {
      if (phases.length === 0) continue;

      // Sort phases: L1, L2, L3, N, Ges
      phases.sort((a, b) => {
        const order = ['L1', 'L2', 'L3', 'N', 'Ges'];
        return order.indexOf(a.phase) - order.indexOf(b.phase);
      });

      // Check if we have total or per-phase data
      const hasTotal = phases.some(p => p.phase === 'Ges');
      const hasPhases = phases.some(p => phaseOrder.includes(p.phase));

      // Determine thresholds based on category
      let maxThreshold: number;
      let warningThreshold: number | undefined;

      // Rated current: from fuse size, or derived from ratingPowerKva
      // I = S / (√3 × U) ≈ kVA × 1.44 at 400V low voltage
      const ratedCurrentA = fuseSizeA ?? (ratingPowerKva != null ? Math.round(ratingPowerKva * 1.44) : null);

      if (category === 'current') {
        if (ratedCurrentA != null && warningPercent != null && criticalPercent != null) {
          maxThreshold = ratedCurrentA * criticalPercent;
          warningThreshold = ratedCurrentA * warningPercent;
          for (const phase of phases) {
            if (phase.phase === 'Ges') {
              phase.maxThreshold = maxThreshold * 3;
              phase.warningThreshold = warningThreshold * 3;
            }
          }
        } else if (ratedCurrentA != null) {
          // Rated current known but no warning/critical percentages
          maxThreshold = ratedCurrentA;
          warningThreshold = undefined;
        } else {
          // No fuse size and no rating power — data-driven scale, no colored zones
          maxThreshold = dataMax(phases);
          warningThreshold = undefined;
        }
      } else if (category === 'reactive_power') {
        if (ratingPowerKva != null) {
          // Reactive power: based on power factor cos(φ) ≥ 0.9
          // At cos(φ) = 0.9, Q ≈ 44% of S (sin(arccos(0.9)) ≈ 0.436)
          // Use 40% for warning, 50% for critical
          if (hasTotal && !hasPhases) {
            maxThreshold = ratingPowerKva * 0.50;
            warningThreshold = ratingPowerKva * 0.40;
          } else {
            const perPhaseKva = ratingPowerKva / 3;
            maxThreshold = perPhaseKva * 0.50;
            warningThreshold = perPhaseKva * 0.40;
            for (const phase of phases) {
              if (phase.phase === 'Ges') {
                phase.maxThreshold = ratingPowerKva * 0.50;
                phase.warningThreshold = ratingPowerKva * 0.40;
              }
            }
          }
        } else {
          // No rating power — data-driven scale, no colored zones
          maxThreshold = dataMax(phases);
          warningThreshold = undefined;
        }
      } else if (category === 'apparent_power' || category === 'active_power') {
        if (ratingPowerKva != null && warningPercent != null && criticalPercent != null) {
          if (hasTotal && !hasPhases) {
            maxThreshold = ratingPowerKva * criticalPercent;
            warningThreshold = ratingPowerKva * warningPercent;
          } else {
            const perPhaseKva = ratingPowerKva / 3;
            maxThreshold = perPhaseKva * criticalPercent;
            warningThreshold = perPhaseKva * warningPercent;
            for (const phase of phases) {
              if (phase.phase === 'Ges') {
                phase.maxThreshold = ratingPowerKva * criticalPercent;
                phase.warningThreshold = ratingPowerKva * warningPercent;
              }
            }
          }
        } else if (ratingPowerKva != null) {
          // Rating known but no warning/critical percentages
          maxThreshold = hasTotal && !hasPhases ? ratingPowerKva : ratingPowerKva / 3;
          warningThreshold = undefined;
        } else {
          // No rating power — data-driven scale, no colored zones
          maxThreshold = dataMax(phases);
          warningThreshold = undefined;
        }
      } else if (category === 'voltage') {
        // Voltage: DIN EN 50160 limits relative to Un = 230 V (253 V; warning at 246,1 V)
        maxThreshold = VOLTAGE_OPERATIONAL_MAX_V;
        warningThreshold = VOLTAGE_WARNING_HIGH_V;
      } else {
        // For other measurements, use max value from data + 20%
        maxThreshold = dataMax(phases);
        warningThreshold = undefined;
      }

      groups.push({
        category,
        label: categoryLabels[category] || category,
        unit: phases[0].unit,
        phases,
        maxThreshold,
        warningThreshold,
      });
    }

    // Sort groups by category importance
    const categoryOrder = ['apparent_power', 'active_power', 'reactive_power', 'current', 'voltage', 'other'];
    groups.sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a.category);
      const bIndex = categoryOrder.indexOf(b.category);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });

    return groups;
  }, [outlet, historyData, startDate, endDate, isLive]);

  // Filter groups based on selected category
  const filteredGroups = useMemo(() => {
    if (categoryFilter === 'all') return measurementGroups;
    return measurementGroups.filter(g => g.category === categoryFilter);
  }, [measurementGroups, categoryFilter]);

  // Get available categories for filter buttons
  const availableCategories = useMemo(() => {
    const cats = new Set(measurementGroups.map(g => g.category));
    return (['all', 'apparent_power', 'active_power', 'reactive_power', 'current', 'voltage'] as CategoryFilter[])
      .filter(c => c === 'all' || cats.has(c));
  }, [measurementGroups]);

  if (measurementGroups.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-primary-400 py-4 text-center">
        Keine Messwerte verfügbar.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {availableCategories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              categoryFilter === cat
                ? 'bg-info-500 text-white'
                : 'bg-gray-100 dark:bg-primary-700 text-gray-700 dark:text-primary-200 hover:bg-gray-200 dark:hover:bg-primary-600 border border-gray-200 dark:border-primary-600'
            }`}
          >
            {filterLabels[cat]}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-600 dark:text-primary-300 px-3 py-2 bg-gray-50 dark:bg-primary-700/50 rounded-lg">
        <div className="flex items-center gap-1.5">
          <div className="w-1 h-4 bg-info-500 rounded" />
          <span>Aktueller Wert</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-6 h-3 bg-gray-500 dark:bg-gray-400 rounded-sm opacity-80" />
          <span>Min-Max Bereich</span>
        </div>
        <div className="flex items-center gap-4 ml-auto">
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-3 bg-green-400 dark:bg-green-600 rounded-sm" />
            <span>Normal</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-3 bg-orange-400 dark:bg-orange-500 rounded-sm" />
            <span>Warnung</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-3 bg-red-400 dark:bg-red-500 rounded-sm" />
            <span>Kritisch</span>
          </div>
        </div>
      </div>

      {filteredGroups.map(group => (
        <div
          key={group.category}
          className="bg-white dark:bg-primary-800 border border-gray-200 dark:border-primary-600 rounded-lg overflow-hidden"
        >
          {/* Group header */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-primary-700 border-b border-gray-200 dark:border-primary-600">
            <h5 className="text-sm font-semibold text-gray-800 dark:text-primary-100">
              {group.label} ({group.unit})
            </h5>
          </div>

          {/* History Line Chart - only shown when specific category is selected */}
          {categoryFilter !== 'all' && (
            <div className="px-4 pt-4">
              <HistoryLineChart
                historyData={historyData}
                category={group.category}
                unit={group.unit}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          )}

          {/* Phase bars */}
          <div className="px-4 py-2">
            {group.phases.map((phase, idx) => (
              <ThresholdBar
                key={`${phase.phase}-${idx}`}
                phase={phase.phase}
                currentValue={phase.currentValue}
                minValue={phase.minValue}
                maxValue={phase.maxValue}
                maxThreshold={phase.maxThreshold ?? group.maxThreshold}
                warningThreshold={phase.warningThreshold ?? group.warningThreshold}
                unit={group.unit}
                allowNegative={group.category === 'reactive_power'}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PhaseBarChart;
